import React from 'react';
import styled from 'styled-components';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #000D1A 0%, #001F3F 100%);
  color: var(--white);
  font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  padding: 22px 0 10px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--gradient-primary);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 18px;
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    text-align: center;
  }
`;

const FooterSection = styled.div``;

const BrandSection = styled(FooterSection)``;

const Brand = styled.div`
  margin-bottom: 0.75rem;
`;

const Logo = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: var(--white);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  .shark-icon {
    color: var(--primary-turquoise);
    margin-right: 10px;
    animation: swim 3s ease-in-out infinite;
  }
  
  @keyframes swim {
    0%, 100% { transform: translateX(0) rotate(0deg); }
    25% { transform: translateX(3px) rotate(1deg); }
    75% { transform: translateX(-3px) rotate(-1deg); }
  }
`;

const BrandDescription = styled.p`
  color: var(--light-turquoise);
  line-height: 1.6;
  margin-bottom: 0.9rem;
  opacity: 0.9;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  width: 34px;
  height: 34px;
  background: rgba(64, 224, 208, 0.1);
  border: 1px solid var(--primary-turquoise);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-turquoise);
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--primary-turquoise);
    color: var(--navy-blue);
    transform: translateY(-3px);
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--white);
  margin-bottom: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &::after {
    content: '';
    display: block;
    width: 30px;
    height: 2px;
    background: var(--primary-turquoise);
    margin-top: 0.35rem;
    
    @media (max-width: 768px) {
      margin: 0.5rem auto 0;
    }
  }
`;

const FooterList = styled.ul`
  list-style: none;
`;

const FooterListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const FooterLink = styled.a`
  color: var(--light-turquoise);
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s ease;
  opacity: 0.9;
  
  &:hover {
    color: var(--primary-turquoise);
    opacity: 1;
    padding-left: 5px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.8rem;
  color: var(--light-turquoise);
  font-size: 0.9rem;
  opacity: 0.9;
  
  i {
    color: var(--primary-turquoise);
    width: 20px;
    margin-right: 10px;
  }
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(64, 224, 208, 0.2);
  padding-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: var(--light-turquoise);
  font-size: 0.8rem;
  opacity: 0.8;
`;

const FooterNav = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
  }
`;

const FooterNavLink = styled.a`
  color: var(--light-turquoise);
  font-size: 0.8rem;
  text-decoration: none;
  transition: color 0.3s ease;
  opacity: 0.8;
  
  &:hover {
    color: var(--primary-turquoise);
    opacity: 1;
  }
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          <BrandSection>
            <Brand>
              <Logo>
                <i className="fas fa-fish shark-icon"></i>
                Pepe Tiburón
              </Logo>
              <BrandDescription>
                La cevichería más auténtica del Perú. Desde 2010 llevando la frescura 
                del mar a tu mesa con la tradición y sabor que nos caracteriza.
              </BrandDescription>
            </Brand>
            
            <SocialLinks>
              <SocialLink href="https://web.facebook.com/pepetiburon01/" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-facebook-f"></i>
              </SocialLink>
              <SocialLink href="https://instagram.com/pepe.tiburon" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-instagram"></i>
              </SocialLink>
              <SocialLink href="https://tiktok.com/@cevicheriapepe_tiburon" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-tiktok"></i>
              </SocialLink>
              <SocialLink href="https://youtube.com/@pepetiburon" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-youtube"></i>
              </SocialLink>
            </SocialLinks>
          </BrandSection>

          <FooterSection>
            <SectionTitle>Nuestros Locales</SectionTitle>
            <FooterList>
              <FooterListItem>
                <FooterLink href="#locales">Selva Alegre</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink href="#locales">Cayma (Micaela Bastidas)</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink href="#locales">Cayma (Ramón Castilla)</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink href="#locales">Cercado</FooterLink>
              </FooterListItem>
            </FooterList>
          </FooterSection>

          <FooterSection>
            <SectionTitle>Especialidades</SectionTitle>
            <FooterList>
              <FooterListItem>
                <FooterLink href="#platos">Cevichop en Copa</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink href="#platos">Ceviche Tradicional</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink href="#platos">Arroz con Mariscos</FooterLink>
              </FooterListItem>
            </FooterList>
          </FooterSection>

          <FooterSection>
            <SectionTitle>Contacto</SectionTitle>
            <ContactInfo>
              <i className="fas fa-envelope"></i>
              contacto@pepetiburon.com
            </ContactInfo>
            <ContactInfo>
              <i className="fas fa-clock"></i>
              Lun - Dom: 10:00 AM - 5:00 PM
            </ContactInfo>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <Copyright>
            © {currentYear} Pepe Tiburón. Todos los derechos reservados. 
            Hecho con ❤️ para los amantes del mar.
          </Copyright>
          
          <FooterNav>
            <FooterNavLink href="#inicio">Política de Privacidad</FooterNavLink>
            <FooterNavLink href="#inicio">Términos de Servicio</FooterNavLink>
            <FooterNavLink href="#contacto">Trabaja con Nosotros</FooterNavLink>
          </FooterNav>
        </FooterBottom>
      </Container>
    </FooterContainer>
  );
};

export default Footer;